/*
 * DesbloqueoConCarta.jsx — Feb 2026 (iter 07-23i)
 *
 * Componente inline dentro de Cliente360 tab Screening. Sólo el Oficial de Cumplimiento
 * (o super_admin) puede desbloquear a un cliente previamente sancionado (bloqueado_lpb=True).
 *
 * NO ES un override de grado Alto.
 *  - El override 43ª (`/riesgo/override`) libera a un cliente con grado ALTO por EBR alto
 *    o PEP confirmado; NO aplica cuando hay coincidencia CONFIRMADA en lista de sanción.
 *  - Este desbloqueo (`/screening/clientes/{cid}/desbloquear`) revierte un bloqueo por
 *    coincidencia en OFAC / SAT 69 / SAT 69-B / ONU / LPB. Es más grave: un cliente
 *    sancionado que se libera del bloqueo requiere carta PDF adicional a la del override.
 *
 * Distintivo visual (para que el OC no lo confunda con el override):
 *  - Card rojo intenso (`border-red-300`, `bg-red-50`) con icono `UnlockKeyhole`.
 *  - Título "Desbloqueo por sanción" (no "Autorización de la mesa (override)").
 *  - Muestra la lista y score reales del bloqueo, con badge rojo.
 *  - Requiere confirmación explícita marcando checkbox "Entiendo que esto revierte un
 *    bloqueo por sanción y quedará auditado".
 */
import { useRef, useState } from "react";
import { UnlockKeyhole, Loader2, Upload, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function DesbloqueoConCarta({ clienteId, bloqueoLista, bloqueoScore, bloqueoMotivo, onDone }) {
  const { user } = useAuth();
  const esOC = ["super_admin", "oficial_cumplimiento"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [confirmo, setConfirmo] = useState(false);
  const [cartaDocId, setCartaDocId] = useState("");
  const [cartaNombre, setCartaNombre] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const fileRef = useRef(null);

  if (!esOC) return null;

  const reset = () => {
    setOpen(false);
    setMotivo("");
    setConfirmo(false);
    setCartaDocId("");
    setCartaNombre("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const subir = async (file) => {
    if (!file) return;
    if (!/pdf/i.test(file.type || "") && !/\.pdf$/i.test(file.name || "")) {
      toast.error("La carta de desbloqueo debe ser un archivo PDF.");
      return;
    }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo_documento", "Carta de autorización (desbloqueo)");
      fd.append("categoria", "desbloqueo");
      fd.append("cliente_id", clienteId);
      fd.append("clasificacion", "Soporte");
      const { data } = await api.post("/documentos/upload", fd);
      setCartaDocId(data.id);
      setCartaNombre(data.nombre_archivo || file.name);
      toast.success("Carta PDF cargada y cifrada.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubiendo(false);
    }
  };

  const desbloquear = async () => {
    if (!cartaDocId) {
      toast.error("Sube la carta de autorización en PDF antes de desbloquear.");
      return;
    }
    if (motivo.trim().length < 10) {
      toast.error("La justificación del desbloqueo es obligatoria (mínimo 10 caracteres).");
      return;
    }
    if (!confirmo) {
      toast.error("Debes confirmar que entiendes las consecuencias del desbloqueo.");
      return;
    }
    setEjecutando(true);
    try {
      const { data } = await api.post(`/screening/clientes/${clienteId}/desbloquear`, {
        motivo: motivo.trim(),
        carta_doc_id: cartaDocId,
      });
      toast.success(
        `Cliente desbloqueado. Registrado ${data?.desbloqueo_fecha || "hoy"} por ${data?.desbloqueo_por || "OC"}.`
      );
      reset();
      onDone && onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div
      className="mb-4 rounded-lg border-2 border-red-300 bg-red-50/50 p-4"
      data-testid="c360-desbloqueo-panel"
    >
      <div className="mb-3 flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-red-700" />
        <h4 className="text-sm font-semibold text-red-900" data-testid="c360-desbloqueo-title">
          Desbloqueo por sanción — decisión regulatoria
        </h4>
        <Badge
          variant="outline"
          className="ml-auto border-red-400 bg-white text-[10px] uppercase tracking-wide text-red-700"
        >
          Distinto del override 43ª
        </Badge>
      </div>
      <p className="mb-3 text-xs text-red-800">
        Este flujo revierte un <b>bloqueo por coincidencia en lista de sanción</b>. NO es un override de grado Alto por EBR: aplica únicamente cuando existe hit en OFAC, ONU, SAT 69 / 69-B o LPB. El acto queda auditado y notificado al panel de Cumplimiento.
      </p>

      <div
        className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-white/70 px-3 py-2 text-xs"
        data-testid="c360-desbloqueo-detalle"
      >
        <span className="font-semibold text-slate-700">Causa del bloqueo actual:</span>
        <Badge className="bg-red-600 text-white hover:bg-red-600">
          {bloqueoLista || "lista de sanción"}
        </Badge>
        {bloqueoScore ? (
          <span className="text-slate-700">
            score <b>{bloqueoScore}</b>
          </span>
        ) : null}
        {bloqueoMotivo ? (
          <span className="text-slate-500 italic">· {bloqueoMotivo}</span>
        ) : null}
      </div>

      {!open ? (
        <Button
          size="sm"
          variant="outline"
          className="border-red-400 bg-white text-red-700 hover:bg-red-100 hover:text-red-800"
          onClick={() => setOpen(true)}
          data-testid="c360-desbloqueo-abrir-btn"
        >
          <UnlockKeyhole className="mr-2 h-4 w-4" /> Iniciar desbloqueo con carta PDF
        </Button>
      ) : (
        <div className="space-y-3 rounded-md border border-red-200 bg-white p-3">
          {/* Carga PDF inline (mismo patrón que override 43ª) */}
          <div>
            <Label className="mb-1 block text-xs font-medium text-slate-700">
              Carta de autorización (PDF firmado por el OC)
            </Label>
            <div className="flex items-center gap-2">
              <Input
                ref={fileRef}
                type="file"
                accept="application/pdf,.pdf"
                disabled={subiendo || !!cartaDocId}
                onChange={(e) => subir(e.target.files?.[0] || null)}
                className="h-9"
                data-testid="c360-desbloqueo-file-input"
              />
              {subiendo && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Subiendo…
                </span>
              )}
            </div>
            {cartaDocId && (
              <p
                className="mt-1 flex items-center gap-1 text-xs text-emerald-700"
                data-testid="c360-desbloqueo-carta-cargada"
              >
                <Upload className="h-3 w-3" /> Carta cargada: <b>{cartaNombre}</b>
              </p>
            )}
          </div>

          {/* Justificación */}
          <div>
            <Label className="mb-1 block text-xs font-medium text-slate-700">
              Justificación del desbloqueo (obligatoria, quedará en bitácora)
            </Label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Fundamento legal, verificación de identidad falsa positiva, acuerdo del comité PLD, etc."
              data-testid="c360-desbloqueo-motivo"
            />
          </div>

          {/* Confirmación explícita */}
          <label
            className="flex cursor-pointer items-start gap-2 rounded-md border border-red-200 bg-red-50/70 px-3 py-2 text-xs text-red-900"
            data-testid="c360-desbloqueo-confirmo-wrap"
          >
            <Checkbox
              checked={confirmo}
              onCheckedChange={(v) => setConfirmo(!!v)}
              data-testid="c360-desbloqueo-confirmo-chk"
            />
            <span>
              Entiendo que este desbloqueo revierte una coincidencia en lista de sanción y queda auditado con mi usuario, fecha y la carta PDF adjunta.
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={reset}
              disabled={ejecutando}
              data-testid="c360-desbloqueo-cancelar-btn"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              disabled={!cartaDocId || !confirmo || motivo.trim().length < 10 || ejecutando}
              onClick={desbloquear}
              data-testid="c360-desbloqueo-confirmar-btn"
            >
              {ejecutando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UnlockKeyhole className="mr-2 h-4 w-4" />
              )}
              Confirmar desbloqueo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
